/**
 * AII-6 (K87-A): pure prefill mapper for the AI-intake → CreateTicket flow.
 *
 * `CreateTicket.handleAiIntakeApply` previously inlined the customer/vehicle
 * field mapping (trim, VIN-uppercase, finite-mileage gate, waiter/drop-off,
 * promised_time → appointment date). That logic was uncovered by tests. This
 * extracts it to a pure, deterministic function so the "extracted intake data
 * flows into the ticket form without silent loss" contract is testable offline.
 *
 * Behavior is identical to the prior inline code — it only returns the fields
 * the extraction actually carries (absent/blank fields are omitted, never
 * defaulted), so the component applies exactly what was extracted. The jobs
 * mapping stays in `matchExtractionToCatalog` (already tested).
 */
import type { Extraction } from '@/components/tickets/AiIntakeAssistDialog';

export interface IntakePrefill {
  customerName?: string;
  customerPhone?: string;
  /** Raw extracted year (used for the make/model dropdown cascade). */
  year?: number;
  make?: string;
  model?: string;
  /** Already upper-cased, as the form stores it. */
  vin?: string;
  licensePlate?: string;
  /** Only present when a finite number was extracted. */
  odometerMiles?: number;
  /** Only Waiter / Drop-off are inferred; otherwise omitted (user keeps theirs). */
  type?: 'Waiter' | 'Drop-off';
  /** yyyy-mm-dd derived from a valid promised_time ISO timestamp. */
  appointmentDate?: string;
  /** Display-only advisor notes; null when none. */
  advisorNotes: string | null;
}

export function buildIntakePrefill(extraction: Extraction): IntakePrefill {
  const prefill: IntakePrefill = { advisorNotes: null };

  // ---------- Customer ----------
  const fullName = (extraction.customer?.full_name ?? '').trim();
  if (fullName) prefill.customerName = fullName;
  const phone = (extraction.customer?.phone ?? '').trim();
  if (phone) prefill.customerPhone = phone;

  // ---------- Vehicle (year/make/model feed the dropdown cascade) ----------
  const year = extraction.vehicle?.year ?? undefined;
  if (year) prefill.year = year;
  const make = (extraction.vehicle?.make ?? '').trim() || undefined;
  if (make) prefill.make = make;
  const model = (extraction.vehicle?.model ?? '').trim() || undefined;
  if (model) prefill.model = model;

  // ---------- VIN / Plate / Mileage ----------
  const vin = (extraction.vehicle?.vin ?? '').trim();
  if (vin) prefill.vin = vin.toUpperCase();
  const plate = (extraction.vehicle?.license_plate ?? '').trim();
  if (plate) prefill.licensePlate = plate;
  const mileage = extraction.vehicle?.mileage;
  if (mileage != null && Number.isFinite(mileage)) prefill.odometerMiles = mileage;

  // ---------- Ticket type ----------
  // Explicit is_waiter true → Waiter; explicit is_drop_off true → Drop-off;
  // anything else leaves the user's current value untouched (omitted here).
  if (extraction.intake?.is_waiter === true) {
    prefill.type = 'Waiter';
  } else if (extraction.intake?.is_drop_off === true) {
    prefill.type = 'Drop-off';
  }

  // ---------- Promised time → appointment date ----------
  const promised = (extraction.intake?.promised_time ?? '').trim();
  if (promised) {
    const d = new Date(promised);
    if (!Number.isNaN(d.getTime())) {
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      prefill.appointmentDate = `${yyyy}-${mm}-${dd}`;
    }
  }

  // ---------- Advisor notes (display-only) ----------
  const advisorNotes = (extraction.intake?.advisor_notes ?? '').trim();
  prefill.advisorNotes = advisorNotes || null;

  return prefill;
}
